/* eslint-disable react-refresh/only-export-components -- провайдер и его хук
   живут вместе: контекст между ними не должен утекать наружу. */
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AuthTokens, TokenStore } from '@dorixona/api'
import type { AuthApi, Customer, TelegramResult } from '../features/auth/api'
import { isTelegramMiniApp, startTelegram, telegramInitData } from './telegram'
import {
  NATIVE_LOGOUT_EVENT,
  type NativeLogoutRequest,
  acknowledgeNativeLogout,
  nativeSignedIn,
  nativeSignedOut,
  requirePinSetup,
  subscribeToNotifications,
  unsubscribeFromNotifications,
} from './nativeBridge'
import { listenPushToken, registerPush, unregisterPush } from './push'
import { env } from '../config/env'

/**
 * Сколько ждать сервер при выходе по просьбе оболочки: она ждёт подтверждения
 * 8 секунд, а токены у нас уже стёрты — сервер лишь гасит refresh-токен.
 */
const NATIVE_LOGOUT_SERVER_WAIT = 3000

/**
 * Чего не хватило для входа мини-аппа: номера (дальше — код или контакт из
 * Telegram) или регистрации, если номер уже пришёл из бота (BR-39).
 */
export type TelegramPending =
  | { kind: 'phone'; telegramToken: string; firstName: string }
  | { kind: 'register'; registrationToken: string; firstName: string }

export function toTelegramPending(result: TelegramResult): TelegramPending | null {
  if (result.status === 'phone_required') {
    return { kind: 'phone', telegramToken: result.telegramToken, firstName: result.firstName }
  }
  if (result.status === 'registration_required') {
    return {
      kind: 'register',
      registrationToken: result.registrationToken,
      firstName: result.firstName,
    }
  }
  return null
}

export interface Session {
  isAuthenticated: boolean
  /** Профиль вошедшего; null, пока он не загружен или вход не выполнен. */
  customer: Customer | null
  signIn: (tokens: AuthTokens) => void
  signOut: () => void
  /** Мини-апп входит сам; если не смог — чего не хватило. На сайте всегда null. */
  telegram: TelegramPending | null
}

const SessionContext = createContext<Session | null>(null)

export interface SessionProviderProps {
  tokenStore: TokenStore
  api: AuthApi
  children: ReactNode
}

/**
 * Кто вошёл. Токены живут в TokenStore (его же читает API-клиент), профиль
 * приходит из `GET /me` и обновляется сам после входа и регистрации.
 *
 * Выход сначала гасит сессию на сервере, а потом чистит токены: если запрос
 * не прошёл, локально всё равно выходим — иначе экран останется запертым.
 */
export function SessionProvider({ tokenStore, api, children }: SessionProviderProps) {
  const [authenticated, setAuthenticated] = useState(() => tokenStore.get() !== null)

  const profile = useQuery({
    queryKey: ['me', authenticated],
    queryFn: () => api.me(),
    enabled: authenticated,
    staleTime: 60_000,
  })

  const [telegram, setTelegram] = useState<TelegramPending | null>(null)

  const signIn = useCallback(
    (tokens: AuthTokens) => {
      tokenStore.set(tokens)
      setTelegram(null)
      setAuthenticated(true)
    },
    [tokenStore],
  )

  // В Telegram входим сами, по подписанной строке запуска: привязанный
  // аккаунт открывается без экрана входа. Не вышло — остаёмся гостем, а экран
  // входа продолжит с того шага, которого не хватило. Один раз на запуск:
  // StrictMode вызывает эффект дважды.
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    startTelegram()
    if (authenticated || !isTelegramMiniApp()) return
    api.telegramLogin(telegramInitData()).then(
      (result) => {
        if (result.status === 'ok') signIn(result.tokens)
        else setTelegram(toTelegramPending(result))
      },
      // Подпись не сошлась или вход в Telegram выключен — обычный вход по номеру.
      () => undefined,
    )
  }, [api, authenticated, signIn])

  // Наше Flutter-приложение не знает, кто вошёл: id отдаём при каждом запуске
  // и после входа — для push и для PIN-кода оболочки, забираем при выходе.
  // PIN-кода у аккаунта нет — оболочка сразу просит его установить.
  const customerId = profile.data?.id ?? 0
  const subscribed = useRef(0)
  useEffect(() => {
    subscribed.current = customerId
    if (!customerId) return
    // Push по токену (VITE_FEATURE_PUSH): закрепляем телефон при каждом
    // запуске с сессией и после входа — токен меняется при переустановке.
    // Без флага — прежняя подписка на тему, пока оболочка её держит.
    if (env.features.push) void registerPush(api)
    else void subscribeToNotifications(customerId)
    void nativeSignedIn(customerId).then(requirePinSetup)
  }, [api, customerId])

  // Firebase сменил токен телефона — закрепляем новый, пока покупатель вошёл.
  useEffect(() => {
    if (!env.features.push || !authenticated) return
    return listenPushToken(api)
  }, [api, authenticated])

  /**
   * Выход: токены стираем сразу, не дожидаясь сервера — не прошёл запрос,
   * локально всё равно выходим. Его ответ отдаём тому, кому есть смысл ждать.
   */
  const endSession = useCallback((): Promise<unknown> => {
    const tokens = tokenStore.get()
    // Телефон отвязываем раньше, чем гасим сессию на сервере: отвязке нужен
    // ещё живой токен доступа. Отвязка не дольше 3 с и не роняет выход.
    let unlinked: Promise<unknown> = Promise.resolve()
    if (!env.features.push) void unsubscribeFromNotifications(subscribed.current)
    else if (tokens) unlinked = unregisterPush(api, tokens.accessToken)
    const revoked = tokens
      ? unlinked.then(() => api.logout(tokens.refreshToken)).catch(() => undefined)
      : undefined
    tokenStore.set(null)
    setAuthenticated(false)
    return Promise.resolve(revoked)
  }, [api, tokenStore])

  const signOut = useCallback(() => {
    void endSession()
    void nativeSignedOut()
  }, [endSession])

  // Оболочка просит выйти: покупатель забыл PIN-код или трижды ошибся. Ошибку
  // показывает она сама — у нас только выход и подтверждение, что он сделан.
  useEffect(() => {
    let closing = false
    const onLogoutRequest = (event: Event) => {
      const request = (event as CustomEvent<NativeLogoutRequest | undefined>).detail
      if (!request?.requestId || closing) return
      closing = true
      const finish = async () => {
        let ok = true
        try {
          const revoked = endSession()
          await nativeSignedOut()
          await Promise.race([revoked, delay(NATIVE_LOGOUT_SERVER_WAIT)])
        } catch {
          // Токены не стёрлись — оболочка оставит экран закрытым.
          ok = false
        }
        await acknowledgeNativeLogout(request.requestId, ok)
        closing = false
      }
      void finish()
    }
    window.addEventListener(NATIVE_LOGOUT_EVENT, onLogoutRequest)
    return () => {
      window.removeEventListener(NATIVE_LOGOUT_EVENT, onLogoutRequest)
    }
  }, [endSession])

  const value = useMemo<Session>(
    () => ({
      isAuthenticated: authenticated,
      customer: profile.data ?? null,
      signIn,
      signOut,
      telegram,
    }),
    [authenticated, profile.data, signIn, signOut, telegram],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

const delay = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession вызван вне SessionProvider')
  return session
}
